import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { Button, message, Modal } from '@ss/mtd-react';
import { isProduction } from '@utils/tools';
import { loadJS } from '@utils/dom';
import loganFn from '@utils/loganFn';
import './index.scss';

// Yoda滑块接入
const yodaSrc = 'https://s0.meituan.net/mxx/yoda/yoda.seed.js';
const yodaOptions = {
  // requestCode: "$requestCode",              // 必须 授权码
  // root: 'zp_yodaLogin_root', // Yoda滑块挂载节点
  succCallbackFun: 'yodaSuccCallbackFun', // 必须 成功回调函数名 字符串格式
  failCallbackFun: 'yodaFailCallbackFun', // 必须 失败回调函数名 字符串格式
  isMobile: false,
};
// eslint-disable-next-line @typescript-eslint/naming-convention
enum yodaLoadEnum {
  unknow = 'unknow',
  success = 'success',
  fail = 'fail',
}

interface IYodaSlideRef {
  isSuccessLoadYodaFn: () => boolean;
  initYoda: (requestCode: string) => void;
}

interface IProps {
  yodaSlideDialogWrapId: string; // yoda滑块挂载id
  yodaSlideSuccCallback: (options: { requestCode: string; responseCode: string }) => void; // yoda滑块验证成功回调
  yodaSlideFailCallback: (options: { code: number; msg: string }) => void; // yoda滑块验证失败回调
}

function YodaSlide(props: IProps, ref) {
  const { yodaSlideDialogWrapId = 'zp_yodaSlide_root', yodaSlideSuccCallback, yodaSlideFailCallback } = props;
  const [isInitedYoda, setIsInitedYoda] = useState(false); // yoda是否已初始化
  const [isPass, setIsPass] = useState(false); // 是否通过yoda检测
  const [isShowYodaModal, setIsShowYodaModal] = useState(false); // 是否通过yoda检测
  const [yodaLoadType, setYodaLoadType] = useState(yodaLoadEnum.unknow); // yoda js 加载结果
  const [yodaDialogInfo, setYodaDialogInfo] = useState({ yodaDialogTitle: '', isShowYodaDialog: false }); // yoda提示弹窗
  const { yodaDialogTitle, isShowYodaDialog } = yodaDialogInfo; // yoda提示弹窗信息
  // 加载yoda js
  function loadYoda() {
    loadJS(
      {
        src: yodaSrc,
        isDefer: true,
      },
      // js加载成功回调
      () => {
        setYodaLoadType(yodaLoadEnum.success);

        // 设置yoda验证成功回调
        // eslint-disable-next-line dot-notation
        window['yodaSuccCallbackFun'] = (options) => {
          // const { requestCode, responseCode } = options;
          loganFn.loganCustomFn('yoda加载成功', { options });
          loganFn.loganReport();
          message.success({
            message: '滑动验证通过',
          });
          setIsPass(true);
          setIsShowYodaModal(false);
          yodaSlideSuccCallback(options);
        };
        // 设置yoda验证失败回调
        // eslint-disable-next-line dot-notation
        window['yodaFailCallbackFun'] = (options) => {
          const { msg } = options || {};
          loganFn.loganCustomFn('yoda加载失败', { options });
          loganFn.loganReport();
          console.log(msg);
          setIsPass(false);
          setIsShowYodaModal(false);
          // 显示错误弹窗提示
          // setYodaDialogInfo({ yodaDialogTitle: msg, isShowYodaDialog: true });
          yodaSlideFailCallback(options);
        };
      },
      // js加载失败回调
      () => {
        loganFn.loganCustomFn('加载yoda脚本失败');
        loganFn.loganReport();
        setYodaLoadType(yodaLoadEnum.fail);
        // 显示错误弹窗提示
        setYodaDialogInfo({ yodaDialogTitle: '滑动验证模块加载失败，请刷新页面重试', isShowYodaDialog: true });
      },
    );
  }

  const loopInit = (newYodaOptions, requestCode) => {
    const interval = setInterval(() => {
      const element = document.getElementById(yodaSlideDialogWrapId);
      if (element) {
        clearInterval(interval);
        const isPro = isProduction();
        const yodaEnv = isPro ? undefined : 'test';
        // @ts-ignore
        window.YodaSeed(newYodaOptions, yodaEnv);
        loganFn.loganCustomFn('yoda初始化', { requestCode, yodaEnv });
      }
    }, 300);
  };

  // yoda 模块初始化
  function initYoda(requestCode) {
    // if (isInitedYoda) {
    //   return;
    // }
    setIsInitedYoda(true);
    setIsShowYodaModal(true);
    const newYodaOptions = Object.assign(yodaOptions, { requestCode, root: yodaSlideDialogWrapId });
    loopInit(newYodaOptions, requestCode);
  }

  // 校验yoda是否加载成功
  function isSuccessLoadYodaFn() {
    if (yodaLoadType === yodaLoadEnum.unknow) {
      loganFn.loganCustomFn('滑动验证模块加载中');
      message.info({
        message: '滑动验证模块加载中，请稍后',
      });
      return false;
    }
    // 滑动模块加载失败
    if (yodaLoadType === yodaLoadEnum.fail) {
      loganFn.loganCustomFn('滑动验证脚本加载失败');
      setYodaDialogInfo({ yodaDialogTitle: '滑动验证脚本加载失败，请刷新页面重试', isShowYodaDialog: true });
      return false;
    }
    return true;
  }

  // 校验yoda是否已初始化
  function isInited() {
    return isInitedYoda;
  }

  // 校验yoda是否已检查成功
  function isPassed() {
    return isPass;
  }

  // 暴露方法给父组件手动调用
  useImperativeHandle(ref, () => {
    return { initYoda, isSuccessLoadYodaFn, isInited, isPassed };
  }, [yodaLoadType, isInitedYoda, isPass]);

  // didMount
  useEffect(() => {
    loadYoda();
  }, []);

  return (
    <div className="zp_yodaSlide">
      {isShowYodaModal && (
        <Modal
          className="zp_yodaSlide_slideModal"
          // closable={false}
          onClose={() => {
            setIsShowYodaModal(false);
          }}
        >
          <Modal.Body>
            <div className="zp_yodaSlide_slide" id={yodaSlideDialogWrapId}>
              滑动模块加载中...
            </div>
          </Modal.Body>
        </Modal>
      )}
      {/* <div id={yodaSlideDialogWrapId} /> */}
      {isShowYodaDialog && (
        <Modal
          title={yodaDialogTitle}
          onClose={() => {
            setYodaDialogInfo({ yodaDialogTitle, isShowYodaDialog: false });
          }}
        >
          <Modal.Footer>
            <Button
              style={{ marginRight: '20px' }}
              onClick={() => {
                setYodaDialogInfo({ yodaDialogTitle, isShowYodaDialog: false });
              }}
            >
              取消
            </Button>
            <Button
              type="primary"
              onClick={() => {
                window.location.reload();
              }}
            >
              刷新页面
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}

export default forwardRef(YodaSlide);
export type { IYodaSlideRef };
